import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { FileText, Download, Plus, Trash2, Loader2, RotateCcw, Users, Save, Send, EyeOff, Eye, CheckCircle2, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { getBackendUrl } from '../lib/api';
import { useRegion } from '../hooks/useRegion';
import { hasCrewSuper } from '../lib/regionConfig';

// CSS to hide number input spinners
const hideSpinnerStyle = `
  .no-spinner::-webkit-outer-spin-button,
  .no-spinner::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .no-spinner[type=number] { -moz-appearance: textfield; }
`;

export default function CrewTab({ job, token, formatCurrency, formatDate, xeroStatus }) {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [originalPOs, setOriginalPOs] = useState([]);
  const [superSummary, setSuperSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPO, setSavingPO] = useState(null);
  const [downloadingPO, setDownloadingPO] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingSummary, setDownloadingSummary] = useState(false);
  const [sendingToXero, setSendingToXero] = useState(false);
  const [sendingSuperToXero, setSendingSuperToXero] = useState(false);

  // Crew super is Australian: anywhere else there is no super panel to show
  const { region } = useRegion();
  const showSuper = hasCrewSuper(region);

  const backendUrl = getBackendUrl();
  const jobId = job?.id;
  const jobUpdatedAt = job?.updated_at;

  const fetchPurchaseOrders = useCallback(async () => {
    if (!jobId || !token) return;
    try {
      const res = await fetch(`${backendUrl}/api/jobs/${jobId}/crew-purchase-orders?_t=${Date.now()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Cache-Control': 'no-cache' },
      });
      if (res.ok) {
        const data = await res.json();
        setPurchaseOrders(data);
        setOriginalPOs(JSON.parse(JSON.stringify(data)));
      }
    } catch (e) {
      console.error('Failed to fetch POs', e);
    }
  }, [jobId, token, backendUrl, jobUpdatedAt]);

  const fetchSuperSummary = useCallback(async () => {
    if (!jobId || !token || !showSuper) {
      setSuperSummary(null);
      return;
    }
    try {
      const res = await fetch(`${backendUrl}/api/jobs/${jobId}/crew-super-summary?_t=${Date.now()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Cache-Control': 'no-cache' },
      });
      if (res.ok) {
        const data = await res.json();
        setSuperSummary(data);
      }
    } catch (e) {
      console.error('Failed to fetch super summary', e);
    }
  }, [jobId, token, backendUrl, jobUpdatedAt, showSuper]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPurchaseOrders(), fetchSuperSummary()]);
      setLoading(false);
    };
    loadData();
  }, [fetchPurchaseOrders, fetchSuperSummary]);

  const savePO = async (crewId, poData) => {
    setSavingPO(crewId);
    try {
      const res = await fetch(`${backendUrl}/api/jobs/${jobId}/crew-purchase-orders/${crewId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(poData),
      });
      if (res.ok) {
        toast.success('Purchase order saved');
        setOriginalPOs(prev => prev.map(po => po.crew_id === crewId ? JSON.parse(JSON.stringify(poData)) : po));
        await fetchSuperSummary();
      } else {
        toast.error('Failed to save purchase order');
      }
    } catch (e) {
      toast.error('Failed to save purchase order');
    } finally {
      setSavingPO(null);
    }
  };

  const resetPO = async (crewId, crewName) => {
    if (!window.confirm(`Reset PO for ${crewName} back to estimate values?`)) return;
    try {
      const res = await fetch(`${backendUrl}/api/jobs/${jobId}/crew-purchase-orders/${crewId}/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const updated = await res.json();
        setPurchaseOrders(prev => prev.map(po => po.crew_id === crewId ? updated : po));
        setOriginalPOs(prev => prev.map(po => po.crew_id === crewId ? JSON.parse(JSON.stringify(updated)) : po));
        toast.success('Purchase order reset to estimate values');
        await fetchSuperSummary();
      } else {
        toast.error('Failed to reset purchase order');
      }
    } catch (e) {
      toast.error('Failed to reset purchase order');
    }
  };

  const downloadPO = async (e, crewId, crewName) => {
    e.stopPropagation();
    setDownloadingPO(crewId);
    try {
      const response = await fetch(`${backendUrl}/api/jobs/${jobId}/crew-po/${crewId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to generate PO');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PO_${job.job_number}_${crewName.replace(/ /g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PO for ${crewName} downloaded`);
    } catch (e) {
      toast.error(`Failed to generate PO for ${crewName}`);
    } finally {
      setDownloadingPO(null);
    }
  };

  const downloadAllPOs = async () => {
    setDownloadingAll(true);
    try {
      const response = await fetch(`${backendUrl}/api/jobs/${jobId}/crew-pos-zip`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to generate POs');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Crew_POs_${job.job_number || 'Job'}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('All crew POs downloaded');
    } catch (e) {
      toast.error('Failed to generate POs');
    } finally {
      setDownloadingAll(false);
    }
  };

  const downloadCrewSummary = async () => {
    setDownloadingSummary(true);
    try {
      const response = await fetch(`${backendUrl}/api/jobs/${jobId}/crew-summary-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to generate crew summary');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Crew_Summary_${job.job_number || 'Job'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Crew summary downloaded');
    } catch (e) {
      toast.error('Failed to generate crew summary');
    } finally {
      setDownloadingSummary(false);
    }
  };

  const sendAllToXero = async () => {
    if (!window.confirm('Send all Crew POs to Xero as Draft Bills?')) return;
    setSendingToXero(true);
    const toastId = toast.loading('Sending Crew POs to Xero...');
    try {
      const res = await fetch(`${backendUrl}/api/jobs/${jobId}/send-crew-pos-to-xero`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to send to Xero', { id: toastId });
        return;
      }
      const failures = (data.results || []).filter(r => !r.success);
      if (failures.length > 0) {
        const failDetails = failures.map(f => `${f.crew_name}: ${f.error}`).join('\n');
        toast.error(`${data.summary}\n${failDetails}`, { id: toastId, duration: 8000 });
      } else {
        toast.success(data.summary || 'Crew POs sent to Xero', { id: toastId });
      }
      // Refresh POs to get updated xero_bill_id etc.
      await fetchPurchaseOrders();
    } catch (e) {
      toast.error('Failed to send Crew POs to Xero', { id: toastId });
    } finally {
      setSendingToXero(false);
    }
  };

  const toggleHidden = async (crewId, currentHidden) => {
    try {
      const res = await fetch(`${backendUrl}/api/jobs/${jobId}/crew-purchase-orders/${crewId}/hidden`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden: !currentHidden }),
      });
      if (res.ok) {
        setPurchaseOrders(prev => prev.map(po => po.crew_id === crewId ? { ...po, hidden: !currentHidden } : po));
        toast.success(!currentHidden ? 'Hidden from Crew Summary' : 'Visible in Crew Summary');
      } else {
        toast.error('Failed to update visibility');
      }
    } catch {
      toast.error('Failed to update visibility');
    }
  };

  const toggleMarkedAsSent = async (crewId, currentSent) => {
    try {
      const res = await fetch(`${backendUrl}/api/jobs/${jobId}/crew-purchase-orders/${crewId}/marked-as-sent`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ marked_as_sent: !currentSent }),
      });
      if (res.ok) {
        setPurchaseOrders(prev => prev.map(po => po.crew_id === crewId ? { ...po, marked_as_sent: !currentSent } : po));
        toast.success(!currentSent ? 'Marked as sent' : 'Unmarked as sent');
      } else {
        toast.error('Failed to update sent status');
      }
    } catch {
      toast.error('Failed to update sent status');
    }
  };

  const updateLineItem = (poIndex, itemIndex, field, value) => {
    setPurchaseOrders(prev => {
      const updated = [...prev];
      const po = { ...updated[poIndex] };
      const items = [...po.line_items];
      items[itemIndex] = { ...items[itemIndex], [field]: value };
      po.line_items = items;
      updated[poIndex] = po;
      return updated;
    });
  };

  const addLineItem = (poIndex) => {
    setPurchaseOrders(prev => {
      const updated = [...prev];
      const po = { ...updated[poIndex] };
      po.line_items = [...po.line_items, {
        id: crypto.randomUUID(),
        type: 'custom',
        description: '',
        rate: 0,
        days: 0,
        quantity: 1,
        day_type: null,
        start_date: null,
      }];
      updated[poIndex] = po;
      return updated;
    });
  };

  const removeLineItem = (poIndex, itemIndex) => {
    setPurchaseOrders(prev => {
      const updated = [...prev];
      const po = { ...updated[poIndex] };
      po.line_items = po.line_items.filter((_, i) => i !== itemIndex);
      updated[poIndex] = po;
      return updated;
    });
  };

  const calcLineItemTotal = (item) => {
    if (item.type === 'expense' || item.type === 'custom') {
      return (item.rate || 0) * (item.quantity || 1);
    }
    return (item.rate || 0) * (item.days || 0) * (item.quantity || 1);
  };

  const calcPOTotal = (po) => {
    return (po.line_items || []).reduce((sum, item) => sum + calcLineItemTotal(item), 0);
  };

  const hasPOChanged = (crewId) => {
    const current = purchaseOrders.find(po => po.crew_id === crewId);
    const original = originalPOs.find(po => po.crew_id === crewId);
    if (!current || !original) return false;
    return JSON.stringify(current.line_items) !== JSON.stringify(original.line_items);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (purchaseOrders.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No crew members on this job.</p>
          <p className="text-xs mt-1">Add crew labour or equipment in the job editor to manage purchase orders.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Inject spinner-hiding CSS */}
      <style>{hideSpinnerStyle}</style>

      {/* Crew Purchase Orders Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Crew Purchase Orders
            </CardTitle>
            <div className="flex gap-2">
              {xeroStatus?.connected && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={sendAllToXero}
                  disabled={sendingToXero}
                  data-testid="send-crew-pos-to-xero-btn"
                >
                  {sendingToXero ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send All to Xero
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={downloadCrewSummary}
                disabled={downloadingSummary}
                data-testid="print-crew-summary-btn"
              >
                {downloadingSummary ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                Print Crew Summary
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadAllPOs}
                disabled={downloadingAll}
                data-testid="download-all-pos-btn"
              >
                {downloadingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download All POs
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {purchaseOrders.map((po, poIndex) => (
              <AccordionItem value={po.crew_id} key={po.crew_id} data-testid={`crew-po-${po.crew_id}`}>
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center justify-between w-full pr-3">
                    <div className="flex items-center gap-3">
                      <span className={`font-semibold text-sm ${po.hidden ? 'opacity-50' : ''}`}>{po.crew_name}</span>
                      {po.crew_role && (
                        <Badge variant="secondary" className="text-xs font-normal">{po.crew_role}</Badge>
                      )}
                      {po.xero_bill_id && (
                        <Badge variant="outline" className="text-xs font-normal text-moss border-moss/40">Xero</Badge>
                      )}
                      {po.marked_as_sent && (
                        <Badge variant="outline" className="text-xs font-normal bg-mist text-foreground border-transparent">Sent</Badge>
                      )}
                      {po.hidden && (
                        <Badge variant="outline" className="text-xs font-normal text-ochre border-ochre/40">Hidden</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm tabular-nums">{formatCurrency(calcPOTotal(po))}</span>
                      <button
                        className="inline-flex items-center justify-center rounded-md p-1.5 hover:bg-accent transition-colors"
                        onClick={(e) => downloadPO(e, po.crew_id, po.crew_name)}
                        disabled={downloadingPO === po.crew_id}
                        data-testid={`download-po-${po.crew_id}`}
                      >
                        {downloadingPO === po.crew_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 text-muted-foreground" />
                        )}
                      </button>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    {/* Line Items Table */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[100px]">Type</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="w-[90px] text-right">Rate</TableHead>
                            <TableHead className="w-[70px] text-right">Days/Hrs</TableHead>
                            <TableHead className="w-[60px] text-right">Qty</TableHead>
                            <TableHead className="w-[120px]">Date</TableHead>
                            <TableHead className="w-[100px] text-right">Total</TableHead>
                            <TableHead className="w-[40px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(po.line_items || []).map((item, itemIndex) => (
                            <TableRow key={item.id} data-testid={`po-line-item-${item.id}`}>
                              <TableCell>
                                <Select
                                  value={item.type}
                                  onValueChange={(v) => updateLineItem(poIndex, itemIndex, 'type', v)}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="labour">Labour</SelectItem>
                                    <SelectItem value="equipment">Equipment</SelectItem>
                                    <SelectItem value="expense">Expense</SelectItem>
                                    <SelectItem value="custom">Custom</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Input
                                  className="h-8 text-sm"
                                  value={item.description}
                                  onChange={(e) => updateLineItem(poIndex, itemIndex, 'description', e.target.value)}
                                  placeholder="Description"
                                  data-testid={`po-item-desc-${itemIndex}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  className="h-8 text-sm text-right font-mono no-spinner"
                                  type="number"
                                  step="0.01"
                                  value={item.rate || ''}
                                  onChange={(e) => updateLineItem(poIndex, itemIndex, 'rate', parseFloat(e.target.value) || 0)}
                                  data-testid={`po-item-rate-${itemIndex}`}
                                />
                              </TableCell>
                              <TableCell>
                                {(item.type === 'expense' || item.type === 'custom') ? (
                                  <span className="text-xs text-muted-foreground text-right block">-</span>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    {item.type === 'labour' && (
                                      <div className="flex flex-col">
                                        <button
                                          type="button"
                                          onClick={() => updateLineItem(poIndex, itemIndex, 'labour_rate_mode', 'day')}
                                          className={`text-[10px] leading-tight transition-colors ${item.labour_rate_mode !== 'hour' ? 'font-semibold text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                        >D</button>
                                        <button
                                          type="button"
                                          onClick={() => updateLineItem(poIndex, itemIndex, 'labour_rate_mode', 'hour')}
                                          className={`text-[10px] leading-tight transition-colors ${item.labour_rate_mode === 'hour' ? 'font-semibold text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                        >H</button>
                                      </div>
                                    )}
                                    <Input
                                      className="h-8 text-sm text-right font-mono no-spinner"
                                      type="number"
                                      step="0.5"
                                      value={item.days || ''}
                                      onChange={(e) => updateLineItem(poIndex, itemIndex, 'days', parseFloat(e.target.value) || 0)}
                                      data-testid={`po-item-days-${itemIndex}`}
                                    />
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <Input
                                  className="h-8 text-sm text-right font-mono no-spinner"
                                  type="number"
                                  min="1"
                                  value={item.quantity || ''}
                                  onChange={(e) => updateLineItem(poIndex, itemIndex, 'quantity', parseInt(e.target.value) || 1)}
                                  data-testid={`po-item-qty-${itemIndex}`}
                                />
                              </TableCell>
                              <TableCell>
                                {item.type === 'labour' ? (
                                  <Input
                                    className="h-8 text-xs"
                                    type="date"
                                    value={item.start_date || ''}
                                    onChange={(e) => updateLineItem(poIndex, itemIndex, 'start_date', e.target.value)}
                                    data-testid={`po-item-date-${itemIndex}`}
                                  />
                                ) : (
                                  <span className="text-xs text-muted-foreground block text-center">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm tabular-nums">
                                {formatCurrency(calcLineItemTotal(item))}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => removeLineItem(poIndex, itemIndex)}
                                  data-testid={`po-item-delete-${itemIndex}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* PO Total */}
                    <div className="flex justify-end pr-12">
                      <div className="text-right">
                        <Label className="text-xs text-muted-foreground">PO Total</Label>
                        <p className="font-mono font-semibold text-lg tabular-nums">{formatCurrency(calcPOTotal(po))}</p>
                      </div>
                    </div>

                    {/* Actions: Add Line Item left, Hide + Mark Sent + Reset + Save right */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addLineItem(poIndex)}
                        data-testid={`add-line-item-${po.crew_id}`}
                      >
                        <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Line Item
                      </Button>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); toggleHidden(po.crew_id, po.hidden); }}
                          title={po.hidden ? 'Show in Crew Summary' : 'Hide from Crew Summary'}
                          data-testid={`toggle-hidden-${po.crew_id}`}
                        >
                          {po.hidden ? <Eye className="w-3.5 h-3.5 mr-1.5" /> : <EyeOff className="w-3.5 h-3.5 mr-1.5" />}
                          {po.hidden ? 'Unhide' : 'Hide'}
                        </Button>
                        <Button
                          size="sm"
                          variant={po.marked_as_sent ? "outline" : "ghost"}
                          className={po.marked_as_sent ? "bg-mist border-transparent" : ""}
                          onClick={(e) => { e.stopPropagation(); toggleMarkedAsSent(po.crew_id, po.marked_as_sent); }}
                          data-testid={`toggle-sent-${po.crew_id}`}
                        >
                          {po.marked_as_sent ? <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> : <Circle className="w-3.5 h-3.5 mr-1.5" />}
                          {po.marked_as_sent ? 'Sent' : 'Mark Sent'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => resetPO(po.crew_id, po.crew_name)}
                          data-testid={`reset-po-${po.crew_id}`}
                        >
                          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to Estimate
                        </Button>
                        <Button
                          size="sm"
                          variant={hasPOChanged(po.crew_id) ? "default" : "outline"}
                          className=""
                          onClick={() => savePO(po.crew_id, po)}
                          disabled={savingPO === po.crew_id}
                          data-testid={`save-po-${po.crew_id}`}
                        >
                          {savingPO === po.crew_id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                          Save PO
                        </Button>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Superannuation Summary */}
      {showSuper && superSummary && superSummary.summary.length > 0 && (
        <Card data-testid="crew-super-summary">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-base">Superannuation Summary</span>
                <Badge variant="secondary" className="font-mono text-xs font-normal">{superSummary.percentage}%</Badge>
              </div>
              <div className="flex items-center gap-2">
                {xeroStatus?.connected && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    setSendingSuperToXero(true);
                    try {
                      const res = await fetch(`${backendUrl}/api/jobs/${jobId}/send-super-to-xero`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                      });
                      const data = await res.json();
                      if (res.ok && data.success) {
                        toast.success(data.summary);
                      } else {
                        toast.error(data.detail || data.summary || 'Failed to send super to Xero');
                      }
                    } catch {
                      toast.error('Failed to send super to Xero');
                    } finally {
                      setSendingSuperToXero(false);
                    }
                  }}
                  disabled={sendingSuperToXero}
                  data-testid="send-super-to-xero-btn"
                >
                  {sendingSuperToXero ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                  Send to Xero
                </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${backendUrl}/api/jobs/${jobId}/super-summary-pdf`, {
                        headers: { 'Authorization': `Bearer ${token}` },
                      });
                      if (!res.ok) throw new Error('Failed to generate PDF');
                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Super_Summary_${job?.job_number || jobId}.pdf`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('Superannuation summary downloaded');
                    } catch {
                      toast.error('Failed to download superannuation summary');
                    }
                  }}
                  data-testid="download-super-summary-btn"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Download Summary
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Crew Member</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Super Fund</TableHead>
                  <TableHead className="text-right">Labour Total</TableHead>
                  <TableHead className="text-right">Super Payable</TableHead>
                  <TableHead className="w-[140px]">Paid Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...superSummary.summary]
                  .sort((a, b) => {
                    if (a.requires_super && !b.requires_super) return -1;
                    if (!a.requires_super && b.requires_super) return 1;
                    return (a.crew_name || '').localeCompare(b.crew_name || '');
                  })
                  .map((entry) => (
                  <TableRow key={entry.crew_id} data-testid={`super-row-${entry.crew_id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{entry.crew_name}</p>
                        {entry.crew_role && <p className="text-xs text-muted-foreground">{entry.crew_role}</p>}
                      </div>
                    </TableCell>
                    {entry.requires_super ? (
                      <>
                        <TableCell>
                          <div className="text-xs space-y-0.5">
                            {entry.date_of_birth && <p><span className="text-muted-foreground">DOB:</span> {formatDate(entry.date_of_birth)}</p>}
                            {entry.tfn && <p><span className="text-muted-foreground">TFN:</span> {entry.tfn}</p>}
                            {entry.address && <p><span className="text-muted-foreground">Address:</span> {entry.address}</p>}
                            {!entry.tfn && !entry.address && !entry.date_of_birth && <span className="text-muted-foreground">-</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs space-y-0.5">
                            <p>{entry.super_fund || 'No fund specified'}</p>
                            {entry.member_number && <p><span className="text-muted-foreground">Member:</span> {entry.member_number}</p>}
                            {entry.usi && <p><span className="text-muted-foreground">USI:</span> {entry.usi}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">
                          {formatCurrency(entry.labour_total)}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">
                          {formatCurrency(entry.super_amount)}
                        </TableCell>
                        <TableCell>
                          <Input
                            type={entry.super_paid_date ? "date" : "text"}
                            className="h-8 text-xs"
                            placeholder=""
                            value={entry.super_paid_date || ''}
                            onFocus={(e) => { e.target.type = 'date'; }}
                            onBlur={(e) => { if (!e.target.value) e.target.type = 'text'; }}
                            onChange={async (e) => {
                              const val = e.target.value;
                              try {
                                await fetch(`${backendUrl}/api/jobs/${jobId}/crew-purchase-orders/${entry.crew_id}/super-paid-date`, {
                                  method: 'PUT',
                                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ super_paid_date: val }),
                                });
                                setSuperSummary(prev => ({
                                  ...prev,
                                  summary: prev.summary.map(s => s.crew_id === entry.crew_id ? { ...s, super_paid_date: val } : s),
                                }));
                                if (val) toast.success(`Paid date saved for ${entry.crew_name}`);
                              } catch {
                                toast.error('Failed to save paid date');
                              }
                            }}
                            data-testid={`super-paid-date-${entry.crew_id}`}
                          />
                        </TableCell>
                      </>
                    ) : (
                      <TableCell colSpan={5}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">
                            {entry.business_name && <span className="font-medium">{entry.business_name}</span>}
                            {entry.business_name && entry.abn && <span className="mx-1.5">|</span>}
                            {entry.abn && <span className="font-mono text-xs">ABN {entry.abn}</span>}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            Company or Trust / Superannuation not required
                          </span>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {/* Total Row */}
                <TableRow className="border-t-2">
                  <TableCell colSpan={5} className="font-semibold text-right">Total Super Payable</TableCell>
                  <TableCell className="text-right font-mono font-semibold tabular-nums">
                    {formatCurrency(superSummary.total_super)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
